import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { forumAPI } from '../utils/api';
import '../styles/pages.css';
import { auth } from '../config/firebase';

const Forum = () => {
  const [forums, setForums] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newForum, setNewForum] = useState({
    title: '',
    description: '',
    category: 'general',
    groupName: '',
    tags: ''
  });

  const firebaseUser = auth.currentUser;
  const navigate = useNavigate();

  const fetchForums = useCallback(async () => {
    try {
      const params = {
        category: filter !== 'all' ? filter : undefined,
        groupName: selectedGroup || undefined,
        search: searchTerm || undefined
      };

      const forumsData = await forumAPI.getAll(params);
      setForums(forumsData);
      setError('');
    } catch (err) {
      console.error('Error fetching forums:', err);
      setError('Failed to load forums');
    }
  }, [filter, selectedGroup, searchTerm])

  const fetchUserGroups = async () => {
    try {
      const groupData = await forumAPI.getUserGroups();
      setGroups(groupData);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  };
  

  const handleCreateForum = async (e) => {
    e.preventDefault();
  
    try {
      const payload = {
        title: newForum.title,
        description: newForum.description,
        category: newForum.category,
        groupName: newForum.category === 'group' ? newForum.groupName : null,
        tags: newForum.tags
          ? newForum.tags.split(',').map(t => t.trim())
          : []
      };
      // console.log('Creating forum with:', {
      //   title: newForum.title,
      //   description: newForum.description,
      //   category: newForum.category,
      //   groupName: newForum.groupName,
      //   tags: newForum.tags
      // });
      
      const createdForum = await forumAPI.create(payload);
      await fetchForums();
      // Close modal
      setShowCreateModal(false);
  
      // Reset form
      setNewForum({
        title: '',
        description: '',
        category: 'general',
        groupName: '',
        tags: ''
      });
  
      // Optimistically add new discussion to list
      setForums(prev => [createdForum, ...prev]);
  
      // Optional: navigate straight into it
      navigate(`/forums/${createdForum.id}`);
    } catch (err) {
      console.error('Failed to create discussion:', err);
      setError('Failed to create discussion');
    }
  };
  

  useEffect(() => {
    if (!firebaseUser) return;
  
    const loadData = async () => {
      try {
        setLoading(true);   // 🔥 loading starts ONCE
  
        await Promise.all([
          fetchForums(),
          fetchUserGroups()
        ]);
  
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);  // 🔥 loading ends AFTER both finish
      }
    };
  
    loadData();
  }, [firebaseUser, fetchForums]);
  





  return (
    <div style={{ background: '#ffffff', minHeight: 'calc(100vh - 70px)', padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '2.25rem', 
              fontWeight: 700, 
              color: '#1a1a1a', 
              marginBottom: '0.5rem',
              letterSpacing: '-0.5px'
            }}>
              Forum Discussions
            </h1>
            <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
              Connect with fellow students and share knowledge
            </p>
          </div>
          <button 
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '0.875rem 1.5rem',
              background: '#1a1a1a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 300ms ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseOver={(e) => e.target.style.background = '#333'}
            onMouseOut={(e) => e.target.style.background = '#1a1a1a'}
          >
            <span style={{ fontSize: '1.2rem' }}>+</span>
            New Discussion
          </button>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: '0.95rem'
          }}>
            {error}
          </div>
        )}

        {/* Filters */}
        <div style={{ 
          background: '#f9f9f9', 
          border: '1px solid #e8e8e8',
          borderRadius: '12px', 
          padding: '1.5rem', 
          marginBottom: '2rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              fontWeight: 600, 
              marginBottom: '0.5rem', 
              color: '#1a1a1a',
              fontSize: '0.9rem'
            }}>
              Category
            </label>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontFamily: 'inherit',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="all">All Categories</option>
              <option value="general">💬 General</option>
              <option value="course">📚 Study</option>
              <option value="announcement">📢 Announcement</option>
            </select>
          </div>

          {groups.length > 0 && (
            <div>
              <label style={{ 
                display: 'block', 
                fontWeight: 600, 
                marginBottom: '0.5rem', 
                color: '#1a1a1a',
                fontSize: '0.9rem'
              }}>
                Group
              </label>
              <div 
                value={selectedGroup} 
                onChange={(e) => setSelectedGroup(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                
                {groups.map((group, index) => (
                  <option key={index} value={group.groupName}>
                    {group.groupName} {group.course?.title ? `(${group.course.title})` : ''}
                  </option>
                ))}
              </div>
            </div>
          )}

          <div style={{ gridColumn: groups.length > 0 ? 'auto' : 'span 2' }}>
            <label style={{ 
              display: 'block', 
              fontWeight: 600, 
              marginBottom: '0.5rem', 
              color: '#1a1a1a',
              fontSize: '0.9rem'
            }}>
              Search
            </label>
            <input
              type="text"
              placeholder="Search discussions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Forum List */}
        {loading && (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              fontSize: '1.1rem',
              color: '#666'
            }}>
              ⏳ Loading discussions…
            </div>
          )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {!loading && forums.length === 0 ? (
            <div style={{
              background: '#f9f9f9',
              border: '2px dashed #e8e8e8',
              borderRadius: '12px',
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1a1a1a', marginBottom: '0.5rem' }}>
                No discussions yet
              </h3>
              <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                Be the first to start a conversation!
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#1a1a1a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Create First Discussion
              </button>
            </div>
          ) : (
            forums.map((forum) => (
              <div 
                key={forum.id}
                onClick={() => navigate(`/forums/${forum.id}`)}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e8e8',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 300ms ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.border = '1px solid #1a1a1a';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.border = '1px solid #e8e8e8';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '1rem',
                  gap: '1rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      {forum.isPinned && (
                        <span style={{ fontSize: '1.2rem' }} title="Pinned">📌</span>
                      )}
                      <h3 style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: 600, 
                        color: '#1a1a1a',
                        margin: 0
                      }}>
                        {forum.title}
                      </h3>
                      {forum.isLocked && (
                        <span style={{ fontSize: '1rem', opacity: 0.6 }} title="Locked">🔒</span>
                      )}
                    </div>
                  </div>
                  {/* {getCategoryBadge(forum.category)} */}
                </div>
                
                <p style={{ 
                  color: '#666', 
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  marginBottom: '1rem'
                }}>
                  {forum.description}
                </p>
                
                {forum.groupName && (
                  <div style={{ marginBottom: '1rem' }}>
                    <span style={{
                      background: '#f3f4f6',
                      color: '#374151',
                      padding: '6px 12px',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span>👥</span> {forum.groupName}
                    </span>
                  </div>
                )}

                {forum.tags && forum.tags.length > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '0.5rem',
                    marginBottom: '1rem'
                  }}>
                    {forum.tags.map((tag, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: '#f9f9f9',
                          color: '#666',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '1rem',
                  fontSize: '0.85rem',
                  color: '#999',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ fontWeight: 500, color: '#666' }}>
                    {/* {getDisplayName(forum.createdBy)} */}
                  </span>
                  <span>•</span>
                  {/* <span>{formatDate(forum.createdAt)}</span> */}
                  <span>•</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>👁</span> {forum.views || 0}
                  </span>
                  <span>•</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>💬</span> {forum.postCount || 0}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Forum Modal */}
      {showCreateModal && (
        <div 
          onClick={() => setShowCreateModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <div style={{
              padding: '2rem',
              borderBottom: '1px solid #e8e8e8',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a1a', margin: 0 }}>
                Create New Discussion
              </h2>
              <button 
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '2rem',
                  color: '#999',
                  cursor: 'pointer',
                  padding: 0,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreateForum} style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: 600, 
                  marginBottom: '0.5rem', 
                  color: '#1a1a1a',
                  fontSize: '0.95rem'
                }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={newForum.title}
                  onChange={(e) => setNewForum({...newForum, title: e.target.value})}
                  required
                  placeholder="Enter discussion title"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: 600, 
                  marginBottom: '0.5rem', 
                  color: '#1a1a1a',
                  fontSize: '0.95rem'
                }}>
                  Description *
                </label>
                <textarea
                  value={newForum.description}
                  onChange={(e) => setNewForum({...newForum, description: e.target.value})}
                  required
                  rows="4"
                  placeholder="Describe what you want to discuss"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: 600, 
                  marginBottom: '0.5rem', 
                  color: '#1a1a1a',
                  fontSize: '0.95rem'
                }}>
                  Category *
                </label>
                <select
                  value={newForum.category}
                  onChange={(e) => setNewForum({...newForum, category: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="general">💬 General</option>
                  <option value="course">📚 Study</option>
                </select>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: 600, 
                  marginBottom: '0.5rem', 
                  color: '#1a1a1a',
                  fontSize: '0.95rem'
                }}>
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={newForum.tags}
                  onChange={(e) => setNewForum({...newForum, tags: e.target.value})}
                  placeholder="e.g., javascript, help, project"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    padding: '0.875rem 1.5rem',
                    background: '#f9f9f9',
                    color: '#1a1a1a',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 300ms ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#f3f3f3'}
                  onMouseOut={(e) => e.target.style.background = '#f9f9f9'}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{
                    padding: '0.875rem 1.5rem',
                    background: '#1a1a1a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 300ms ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#333'}
                  onMouseOut={(e) => e.target.style.background = '#1a1a1a'}
                >
                  Create Discussion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forum;
